import { CmsComponentProps } from '@/types/cms';
import { parseJson } from '@/utils/parseJson';
import { CSSProperties, FC, Fragment, useMemo } from 'react';
import { isValidArray } from '@/utils/isValidArray';
import { useComponentTheme } from '../../hooks/useComponentTheme';
import { twMerge } from 'tailwind-merge';
import { CroppedImage } from '@weee-fe/module';

type TextArrayItemProps = {
  icon_url: string;
  text: string;
  version: 'v2' | string;
  htmlStyle?: CSSProperties;
};

const TextArrayItem: FC<TextArrayItemProps> = ({ icon_url, text, version, htmlStyle = {} }) => {
  return (
    <div
      className={twMerge(
        'box-border flex items-center basis-[calc(50%-8px)]',
        version === 'v2' ? '' : 'odd:mr-400 mb-enki4'
      )}>
      <CroppedImage
        url={icon_url}
        width={20}
        height={20}
        configs={{
          renderHeight: 20
        }}
        aria-hidden={true}
        alt=""
        wrapperClassName="mr-2.5 w-5"
      />
      <span
        className={twMerge('text-surface-100-fg-minor', version === 'v2' ? 'enki-body-2xs-medium' : 'enki-body-2xs')}
        style={htmlStyle}>
        {text}
      </span>
    </div>
  );
};

const TextArrayView: FC<Partial<CmsComponentProps>> = ({ properties, cmsComponentKey, pageTheme }) => {
  const { background: bgInfo } = useComponentTheme({ properties, pageTheme });
  const { text_array, text_array_style } = properties;
  const version = properties?.style || '';
  const textArrayStyle = !!text_array_style ? JSON.parse(text_array_style) : {};

  const textArray = useMemo(() => {
    if (!text_array) return [];
    return parseJson(text_array);
  }, [text_array]);

  if (!isValidArray(textArray)) return null;

  if (version === 'v2')
    return (
      <div
        role="list"
        data-module={cmsComponentKey}
        data-version={version}
        className="overflow-hidden px-5 py-2.5 flex items-center min-h-[50px] box-border"
        style={bgInfo?.type === 'color' && bgInfo?.value ? { backgroundColor: bgInfo.value } : {}}>
        {textArray?.map((item, index) => {
          return (
            <Fragment key={`textArrayItem_${index}`}>
              <TextArrayItem
                {...item}
                version={version}
                htmlStyle={!!textArrayStyle?.color ? { color: textArrayStyle?.color } : {}}
              />
              {index !== textArray.length - 1 && (
                <div
                  aria-hidden="true"
                  style={{
                    width: '1px',
                    height: '30px',
                    margin: '0 15px',
                    background: textArrayStyle?.color || '#fff'
                  }}>
                  &nbsp;
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    );

  return (
    <div data-module={cmsComponentKey} data-version={version} className="overflow-hidden px-5 pt-100 pb-2">
      <div className="flex flex-wrap justify-between -mb-enki4" role="list">
        {textArray?.map((item, index) => {
          return <TextArrayItem key={`textArrayItem_${index}`} {...item} version={version} role="listitem" />;
        })}
      </div>
    </div>
  );
};

export default TextArrayView;
